'use client';

import type {
    MfaLoginResponse,
    MfaSetupResponse,
    MfaStatusResponse,
    MfaVerifySetupResponse,
} from '@tracedocuments/types';
import { getAuthClientPlatform, getAuthToken, setAuthSession } from './auth';

// ---------------------------------------------------------------------------
// MFA API functions
// ---------------------------------------------------------------------------

/**
 * `apiFetchFn` is injected at runtime by @tracedocuments/api to avoid
 * circular dependency (auth ↔ api). Falls back to plain fetch when not set.
 */
let _apiFetch: ((path: string, init?: RequestInit) => Promise<Response>) | null = null;

export function setMfaApiFetch(fn: (path: string, init?: RequestInit) => Promise<Response>): void {
    _apiFetch = fn;
}

let _apiUrl: string | null = null;
const MFA_REQUEST_TIMEOUT_MS = 15_000;

export function setMfaApiUrl(url: string): void {
    _apiUrl = url;
}

function getApiUrl(): string {
    // Same escape-hatch as packages/api getApiBaseUrl — e2e injects '' for same-origin mocks.
    if (typeof window !== 'undefined') {
        const override = (window as { __API_BASE_URL_OVERRIDE__?: string }).__API_BASE_URL_OVERRIDE__;
        if (typeof override === 'string') return override;
    }
    return _apiUrl ?? process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:5090';
}

async function publicFetchWithTimeout(path: string, init?: RequestInit): Promise<Response> {
    const controller = new AbortController();
    const onParentAbort = () => controller.abort();
    if (init?.signal) {
        if (init.signal.aborted) controller.abort();
        else init.signal.addEventListener('abort', onParentAbort, { once: true });
    }
    const timeoutId = setTimeout(() => controller.abort(), MFA_REQUEST_TIMEOUT_MS);

    try {
        return await fetch(`${getApiUrl()}${path}`, { ...init, signal: controller.signal });
    } finally {
        clearTimeout(timeoutId);
        init?.signal?.removeEventListener('abort', onParentAbort);
    }
}

function callApi(path: string, init?: RequestInit): Promise<Response> {
    if (_apiFetch) return _apiFetch(path, init);
    return publicFetchWithTimeout(path, init);
}

export async function mfaSetup(): Promise<MfaSetupResponse> {
    const res = await callApi('/api/auth/mfa/setup', { method: 'POST' });
    if (!res.ok) throw new Error('MFA setup failed');
    return res.json();
}

export async function mfaVerifySetup(code: string): Promise<MfaVerifySetupResponse> {
    const res = await callApi('/api/auth/mfa/verify-setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
    });
    if (!res.ok) throw new Error('MFA verify setup failed');
    return res.json();
}

export async function mfaDisable(code: string): Promise<void> {
    const res = await callApi('/api/auth/mfa/disable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
    });
    if (!res.ok) throw new Error('MFA disable failed');
}

export async function mfaGetStatus(): Promise<MfaStatusResponse> {
    const res = await callApi('/api/auth/mfa/status');
    if (!res.ok) throw new Error('MFA status failed');
    return res.json();
}

export async function mfaVerifyLogin(mfaToken: string, code: string): Promise<MfaLoginResponse> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const platform = getAuthClientPlatform();
    if (platform) headers['X-TraceDocuments-Client-Platform'] = platform;

    const res = await publicFetchWithTimeout('/api/auth/mfa/verify-login', {
        method: 'POST',
        headers,
        body: JSON.stringify({ code, mfaToken }),
    });
    if (!res.ok) throw new Error('MFA login verification failed');
    return res.json();
}

export async function mfaStepUp(code: string): Promise<{ token: string; refreshToken: string }> {
    const res = await callApi('/api/auth/mfa/step-up', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
    });
    if (!res.ok) throw new Error('MFA step-up failed');
    const data = await res.json();
    setAuthSession(data.token, data.refreshToken);
    return data;
}

export function hasFreshMfaClaim(maxAgeMinutes = 15): boolean {
    const token = getAuthToken();
    if (!token) return false;

    try {
        const payloadBase64 = token.split('.')[1];
        if (!payloadBase64) return false;

        const payload = JSON.parse(atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/')));
        const verifiedAt = payload.mfa_verified_at;
        if (!verifiedAt) return false;

        const verifiedDate = new Date(verifiedAt).getTime();
        return Date.now() - verifiedDate < maxAgeMinutes * 60 * 1000;
    } catch {
        return false;
    }
}

// Re-export types
export type {
    MfaLoginResponse,
    MfaSetupResponse,
    MfaStatusResponse,
    MfaVerifySetupResponse
} from '@tracedocuments/types';

